/**
 * ProfilePage.tsx
 * Pagina profilo utente con informazioni personali e statistiche
 */
import React from 'react';
import {
  Container,
  SimpleGrid,
  Text,
  Group,
  Stack,
  Avatar,
  Badge,
  ThemeIcon,
  Loader,
  Center,
  Paper,
  Box,
  Divider,
  Timeline,
} from '@mantine/core';
import {
  IconUser,
  IconMail,
  IconId,
  IconClipboardList,
  IconClipboardCheck,
  IconFileDescription,
  IconCalendar,
  IconShield,
  IconActivity,
} from '@tabler/icons-react';
import { GinoriCard, GinoriTitle, GinoriDivider } from '../../components/ui';
import { useOktaAuth } from '../../providers/okta-auth-provider';
import { UserRole } from '../../types/api';

/**
 * Componente per visualizzare una statistica singola
 */
interface StatItemProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
}

function StatItem({ label, value, icon, color }: StatItemProps) {
  return (
    <GinoriCard interactive>
      <Group justify="space-between" align="center">
        <Stack gap="xs">
          <Text
            size="sm"
            c="dimmed"
            fw={400}
            tt="uppercase"
            style={{
              fontFamily: '"Playfair Display", serif',
              letterSpacing: '0.8px',
            }}
          >
            {label}
          </Text>
          <Text
            fw={400}
            size="xl"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: '1.75rem',
            }}
          >
            {value}
          </Text>
        </Stack>
        <ThemeIcon
          color={color}
          variant="light"
          radius="sm"
          size="lg"
          style={{
            backgroundColor: 'var(--mantine-color-ginoriNeutral-1)',
          }}
        >
          {icon}
        </ThemeIcon>
      </Group>
    </GinoriCard>
  );
}

/**
 * Componente per visualizzare informazioni utente
 */
interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | React.ReactNode;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <Group gap="md" py="sm">
      <ThemeIcon variant="light" size="md" radius="sm" color="gray">
        {icon}
      </ThemeIcon>
      <Stack gap={0} style={{ flex: 1 }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
          {label}
        </Text>
        <Text size="sm" fw={400}>
          {value}
        </Text>
      </Stack>
    </Group>
  );
}

/**
 * Mappa ruoli a nomi leggibili
 * Nota: UserRole ha alias con stesso valore numerico, quindi usiamo Map con numero
 */
function getRoleName(role: UserRole): string {
  const roleNames: Map<number, string> = new Map([
    [UserRole.Admin, 'Amministratore'],
    [UserRole.AdminUfficioStile, 'Capo Ufficio Stile'],
    [UserRole.OperatoreUfficioStile, 'Operatore Ufficio Stile'],
    [UserRole.AdminSviluppo, 'Capo Industrializzazione'],
    [UserRole.OperatoreSviluppo, 'Operatore Industrializzazione'],
    [UserRole.User, 'Utente'],
  ]);
  return roleNames.get(role) || 'Ruolo Sconosciuto';
}

/**
 * Mappa ruoli a colori
 */
function getRoleColor(role: UserRole): string {
  const roleColors: Map<number, string> = new Map([
    [UserRole.Admin, 'red'],
    [UserRole.AdminUfficioStile, 'blue'],
    [UserRole.OperatoreUfficioStile, 'cyan'],
    [UserRole.AdminSviluppo, 'orange'],
    [UserRole.OperatoreSviluppo, 'yellow'],
    [UserRole.User, 'gray'],
  ]);
  return roleColors.get(role) || 'gray';
}

/**
 * Ottiene le iniziali dal nome utente
 */
function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

export function ProfilePage() {
  const { userInfo, isLoading, oktaUserInfo, isAdmin, isAdminUfficioStile, isAdminSviluppo } = useOktaAuth();

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!userInfo) {
    return (
      <Container size="md" py="xl">
        <Center>
          <Text c="dimmed">Impossibile caricare il profilo utente.</Text>
        </Center>
      </Container>
    );
  }

  const primaryRole = userInfo.roles?.[0] || UserRole.User;

  // Descrizione ruolo basata sui permessi
  const getRoleDescription = (): string => {
    if (isAdmin) {
      return 'Accesso completo a tutte le funzionalita del sistema, gestione utenti e configurazioni.';
    }
    if (isAdminUfficioStile) {
      return 'Gestione delle richieste di bozzetti e supervisione del team Ufficio Stile.';
    }
    if (isAdminSviluppo) {
      return 'Gestione delle campionature e produzione, supervisione del team Industrializzazione.';
    }
    return 'Accesso alle funzionalita base del sistema per la gestione delle attivita assegnate.';
  };

  return (
    <Container size="xl" py="xl">
      <GinoriTitle elegant subtitle="Visualizza le tue informazioni personali e le statistiche">
        Il Mio Profilo
      </GinoriTitle>

      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
        {/* Colonna sinistra - Info utente */}
        <Box style={{ gridColumn: 'span 1' }}>
          <GinoriCard elevated>
            {/* Avatar e nome */}
            <Stack align="center" gap="md" mb="lg">
              <Avatar
                size={120}
                radius={120}
                color={getRoleColor(primaryRole)}
                style={{
                  border: '3px solid var(--mantine-color-ginoriGold-3)',
                }}
              >
                {getInitials(userInfo.firstName, userInfo.lastName)}
              </Avatar>

              <Stack align="center" gap={4}>
                <Text
                  size="xl"
                  fw={400}
                  style={{
                    fontFamily: '"Playfair Display", serif',
                  }}
                >
                  {userInfo.displayName || `${userInfo.firstName} ${userInfo.lastName}`}
                </Text>

                <Badge color={getRoleColor(primaryRole)} variant="light" size="lg">
                  {getRoleName(primaryRole)}
                </Badge>
              </Stack>
            </Stack>

            <Divider my="md" />

            {/* Informazioni dettagliate */}
            <Stack gap={0}>
              <InfoRow
                icon={<IconMail size={16} />}
                label="Email"
                value={userInfo.email}
              />
              <InfoRow
                icon={<IconId size={16} />}
                label="ID Utente"
                value={String(userInfo.id)}
              />
              {userInfo.upn && (
                <InfoRow
                  icon={<IconUser size={16} />}
                  label="UPN"
                  value={userInfo.upn}
                />
              )}
              <InfoRow
                icon={<IconShield size={16} />}
                label="Ruoli"
                value={
                  <Group gap={4}>
                    {userInfo.roles?.map((role, index) => (
                      <Badge key={index} size="xs" color={getRoleColor(role)} variant="outline">
                        {getRoleName(role)}
                      </Badge>
                    )) || (
                      <Badge size="xs" color="gray" variant="outline">
                        Nessun ruolo
                      </Badge>
                    )}
                  </Group>
                }
              />
            </Stack>
          </GinoriCard>

          {/* Card descrizione ruolo */}
          <GinoriCard elevated mt="lg">
            <Group gap="sm" mb="sm">
              <ThemeIcon variant="light" size="md" radius="sm" color={getRoleColor(primaryRole)}>
                <IconActivity size={16} />
              </ThemeIcon>
              <Text
                size="sm"
                fw={500}
                tt="uppercase"
                style={{
                  fontFamily: '"Playfair Display", serif',
                  letterSpacing: '0.5px',
                }}
              >
                Il tuo ruolo
              </Text>
            </Group>
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
              {getRoleDescription()}
            </Text>
          </GinoriCard>
        </Box>

        {/* Colonna destra - Statistiche e permessi */}
        <Box style={{ gridColumn: 'span 2' }}>
          {/* Statistiche */}
          <Text
            size="lg"
            fw={400}
            mb="md"
            style={{
              fontFamily: '"Playfair Display", serif',
            }}
          >
            Le tue statistiche
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
            <StatItem
              label="Task Assegnati"
              value="-"
              icon={<IconClipboardList size={20} />}
              color="blue"
            />
            <StatItem
              label="Task Completati"
              value="-"
              icon={<IconClipboardCheck size={20} />}
              color="green"
            />
            <StatItem
              label="Richieste Create"
              value="-"
              icon={<IconFileDescription size={20} />}
              color="orange"
            />
            <StatItem
              label="Ultimo Accesso"
              value={new Date().toLocaleDateString('it-IT')}
              icon={<IconCalendar size={20} />}
              color="violet"
            />
          </SimpleGrid>

          <GinoriDivider elegant />

          {/* Permessi utente */}
          <Text
            size="lg"
            fw={400}
            mb="md"
            mt="lg"
            style={{
              fontFamily: '"Playfair Display", serif',
            }}
          >
            I tuoi permessi
          </Text>

          <GinoriCard elevated>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <PermissionItem
                label="Gestione Richieste"
                enabled={isAdmin || isAdminUfficioStile || isAdminSviluppo}
              />
              <PermissionItem
                label="Gestione Task"
                enabled={true}
              />
              <PermissionItem
                label="Visualizza Statistiche"
                enabled={isAdmin || isAdminUfficioStile || isAdminSviluppo}
              />
              <PermissionItem
                label="Gestione Utenti"
                enabled={isAdmin}
              />
              <PermissionItem
                label="Configurazione Sistema"
                enabled={isAdmin}
              />
              <PermissionItem
                label="Export Dati"
                enabled={isAdmin || isAdminUfficioStile || isAdminSviluppo}
              />
            </SimpleGrid>
          </GinoriCard>

          {/* Info Okta se disponibili */}
          {oktaUserInfo && (
            <>
              <GinoriDivider elegant />
              <Text
                size="lg"
                fw={400}
                mb="md"
                mt="lg"
                style={{
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Informazioni Account SSO
              </Text>

              <GinoriCard elevated>
                <Text size="sm" c="dimmed" mb="sm">
                  Il tuo account e gestito tramite Kering SSO (Okta). Le modifiche alle informazioni
                  personali devono essere effettuate attraverso il portale aziendale.
                </Text>
                <Group gap="xs">
                  <Badge color="green" variant="light">
                    SSO Attivo
                  </Badge>
                  <Badge color="blue" variant="light">
                    Autenticazione Kering
                  </Badge>
                </Group>
              </GinoriCard>
            </>
          )}
        </Box>
      </SimpleGrid>
    </Container>
  );
}

/**
 * Componente per visualizzare un permesso
 */
interface PermissionItemProps {
  label: string;
  enabled: boolean;
}

function PermissionItem({ label, enabled }: PermissionItemProps) {
  return (
    <Group gap="sm">
      <Box
        w={8}
        h={8}
        style={{
          borderRadius: '50%',
          backgroundColor: enabled
            ? 'var(--mantine-color-green-6)'
            : 'var(--mantine-color-gray-4)',
        }}
      />
      <Text size="sm" c={enabled ? undefined : 'dimmed'}>
        {label}
      </Text>
    </Group>
  );
}

export default ProfilePage;
